import { useAuth } from '@/context/AuthContext';
import React, { ReactNode, useCallback, useState } from 'react';
import { Tr, Td, Text, useDisclosure, Flex, Box, Tooltip, Spinner } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { QuizLibrary } from '../../types';
import { DeleteQuizModal } from '@/features/Quiz/components/DeleteQuizModal';
import Icon from '@/components/Icon';

import { format } from 'date-fns';
import { useQti } from '@/api/qtiApi';
import InstitutionsModal from '@/features/Playlists/components/InstitutionsModal';
import { usePlaylistCC } from '@/api/ccApi';
import { InstitutionModalTypes } from '@/features/Playlists';
import { usePlaylistScorm } from '@/api/scormApi';
import { useNotificationStore } from '@/stores/notifications';
import { DistributeQuizModal } from '../DistributeQuizModal';
import { saveSharedCustomerQuizById, shareAdminQuizV2 } from '@/features/Quiz/api/getQuizzes';
import { AssignQuizToUsersRequest } from '@/features/Quiz/types';

type Props = {
    quiz: QuizLibrary;
    isAdminQuiz: boolean;
};

const TextEl = ({ children }: { children: ReactNode }) => (
    <Text color="black" lineHeight={'21px'} textAlign="center" display="inline-block" p="0 10px" w={'auto'}>
        {children}
    </Text>
);

export const CustomerQuizItem = ({ quiz, isAdminQuiz }: Props) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { user } = useAuth();
    const isSuperAdmin = user?.roles.includes('ROLE_SUPER_ADMIN');

    const { isOpen: isInstitutionsOpen, onOpen: onInstitutionsOpen, onClose: onInstitutionsClose } = useDisclosure();
    const [type, setType] = useState<InstitutionModalTypes>(null);
    const { addNotification } = useNotificationStore();
    const [error, setError] = useState('');
    const [openDistributeQuiz, setOpenDistributeQuiz] = useState(false);
    const downloadType = 'from_quiz';

    const { isLoading: isQtiLoading, handleQtiDownload } = useQti({
        qtiType: downloadType,
        qtiId: quiz?.id ?? 0,
        title: quiz?.title ?? '',
    });

    const { isLoading: isCCLoading, handleCCDownload } = usePlaylistCC({
        ccType: downloadType,
        ccId: quiz?.id ?? 0,
        title: quiz?.title ?? '',
    });

    const { isLoading: isScormLoading, handleScormDownload } = usePlaylistScorm({
        scormType: downloadType,
        scormId: quiz?.id ?? 0,
        playlistTitle: quiz?.title ?? '',
    });

    const handleOpenInstitutionsCC = () => {
        setType('cc');
        onInstitutionsOpen();
    };

    const handleOpenInstitutionsScorm = () => {
        setType('scorm');
        onInstitutionsOpen();
    };

    const handleOpenInstitutionsQti = () => {
        setType('qti');
        onInstitutionsOpen();
    };


    const handleDistributeQuizClose = useCallback(() => {
        setOpenDistributeQuiz(false);
        setError('')
    }, []);

    const handleDistributeQuizOpen = useCallback((e: any) => {
        e.preventDefault();

        setOpenDistributeQuiz(true);
    }, []);

    const handleDistributeQuizSubmit = useCallback(
        async (data: AssignQuizToUsersRequest) => {
            try {
                if (quiz.quizUuid) {
                    const res = await shareAdminQuizV2(quiz.quizUuid, { emails: data.emails });
                    const notFound = (res as { notFound?: string[] }).notFound ?? [];
                    const notAccess = (res as { notAccess?: string[] }).notAccess ?? [];
                    const shared =
                        (res as { sharedQuizzes?: { sharedWithUserEmail?: string | null }[] }).sharedQuizzes ?? [];
                    const already =
                        (res as { alreadySharedQuizzes?: { sharedWithUserEmail?: string | null }[] })
                            .alreadySharedQuizzes ?? [];

                    if (notFound.length) {
                        addNotification({
                            type: 'error',
                            title: `Not found: ${notFound.join(', ')}`,
                        });
                    }
                    if (notAccess.length) {
                        addNotification({
                            type: 'error',
                            title: `Not access: ${notAccess.join(', ')}`,
                        });
                    }
                    if (already.length) {
                        const emails = already.map((r) => r.sharedWithUserEmail).filter(Boolean);
                        if (emails.length) {
                            addNotification({
                                type: 'error',
                                title: `Already shared: ${emails.join(', ')}`,
                            });
                        }
                    }
                    if (shared.length) {
                        const emails = shared.map((r) => r.sharedWithUserEmail).filter(Boolean);
                        if (emails.length) {
                            addNotification({
                                type: 'success',
                                title: `Shared: ${emails.join(', ')}`,
                            });
                        }
                    }
                } else {
                    const res = await saveSharedCustomerQuizById(quiz?.id ?? 0, data);
                    if (res.already_shared.length) {
                        addNotification({
                            type: 'error',
                            title: `Already shared: ${res.already_shared.join(', ')}`,
                        });
                    }
                    if (res.shared.length) {
                        addNotification({
                            type: 'success',
                            title: `Shared: ${res.shared.join(', ')}`,
                        });
                    }
                    if (res.not_access.length) {
                        addNotification({
                            type: 'error',
                            title: `Not access: ${res.not_access.join(', ')}`,
                        });
                    }
                    if (res.not_found.length) {
                        addNotification({
                            type: 'error',
                            title: `Not found: ${res.not_found.join(', ')}`,
                        });
                    }
                }
                setError('');
            } catch (e: unknown) {
                const message =
                    e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : '';
                setError(message);
                throw e;
            }
        },
        [addNotification, quiz.id, quiz.quizUuid]
    );

    return (
        <>
            <InstitutionsModal
                isOpen={isInstitutionsOpen}
                onClose={onInstitutionsClose}
                handleScormDownload={handleScormDownload}
                handleCCDownload={handleCCDownload}
                handleQtiDownload={handleQtiDownload}
                isLoading={isScormLoading || isCCLoading || isQtiLoading}
                type={type}
            />
            <DeleteQuizModal isOpen={isOpen} onClose={onClose} quiz={quiz} quizType={'customer'} />
            <Tr>
                <Td w={'65%'} py={'10px'}>
                    <Flex as={Link} to={`/quiz/customer-quiz/view/${quiz.id}`} py={'10px'}>
                        <Text color={'black'} lineHeight={'21px'}>
                            {quiz.title}
                        </Text>
                    </Flex>
                </Td>

                <Td w={'15%'} textAlign="center">
                    <TextEl>{quiz?.restrictNonInstitutional ? 'Restricted' : 'Open'}</TextEl>
                </Td>

                <Td w={'15%'} textAlign="center">
                    <TextEl>{`${quiz.createdBy?.firstName ?? ''} ${quiz.createdBy?.lastName ?? ''}`}</TextEl>
                </Td>
                <Td w={'15%'} textAlign="center">
                    <TextEl>{quiz?.createdBy?.email ?? '-'}</TextEl>
                </Td>
                <Td w={'15%'} textAlign="center">
                    <TextEl>{quiz.createdAt ? format(new Date(quiz.createdAt), 'MM/dd/yyyy') : '-'}</TextEl>
                </Td>
                <Td w={'15%'} textAlign="center">
                    <TextEl>{quiz?.endDate ? format(new Date(quiz.endDate), 'MM/dd/yyyy') : quiz?.endDate === null ? '-' : 'Expired'}</TextEl>
                </Td>
                <Td w="auto">
                    <Flex columnGap={'10px'} align="center" justifyContent={'space-around'}>
                        {isSuperAdmin && (
                            <>
                            <Box
                            variant="playlistSmBtn"
                            borderRadius={'16px'}
                            height={'28px'}
                            onClick={handleDistributeQuizOpen}
                        >
                            <Tooltip hasArrow label={'Share quiz'} aria-label={'Share quiz'}>
                                <span><Icon highlightOnHover icon='share-from-square' /></span>
                            </Tooltip>
                        </Box>
                        <DistributeQuizModal
                isOpen={openDistributeQuiz}
                isCustomerQuiz={true}
                onClose={handleDistributeQuizClose}
                onSubmit={handleDistributeQuizSubmit}
                quiz={quiz}
                errorMessage={error}
                sharedList={!isSuperAdmin ? quiz.sharedList : null}
            />
                                <Box
                                    as={Link}
                                    to={quiz.editable ? `/quiz/customer-quiz/edit/${quiz.id}` : `/quiz/customer-quiz/copy/${quiz.id}`}
                                    borderRadius={'16px'}
                                    height={'28px'}>
                                    <Tooltip hasArrow label={quiz.editable ? 'Edit quiz' : 'Copy'} aria-label={quiz.editable ? 'Edit quiz' : 'Copy'}>
                                        <span>
                                            <Icon highlightOnHover icon={quiz.editable ? 'pencil' : 'clone'} />
                                        </span>
                                    </Tooltip>
                                </Box>
                            </>
                        )}
                        <Box onClick={handleOpenInstitutionsScorm}>
                            <Tooltip hasArrow label={'Download SCORM'} aria-label={'Download SCORM'}>
                                {isScormLoading ? (
                                    <Spinner size={'xs'} />
                                ) : (
                                    <span>
                                        <Icon highlightOnHover icon="file-zipper" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>
                        <Box as={Link} to={`/quiz/customer-quiz/results/${quiz.id}`} borderRadius={'16px'} height={'28px'}>
                            <Tooltip hasArrow label={'View Results'} aria-label={'View Results'}>
                                <span>
                                    <Icon highlightOnHover icon="chart-mixed" />
                                </span>
                            </Tooltip>
                        </Box>
                        <Box onClick={handleOpenInstitutionsQti}>
                            <Tooltip hasArrow label={'Download QTI'} aria-label={'Download QTI'}>
                                <span>
                                    <Icon fontSize={'20px'} highlightOnHover icon="scroll" />
                                </span>
                            </Tooltip>
                        </Box>
                        <Box onClick={handleOpenInstitutionsCC}>
                            <Tooltip hasArrow label={'Download CC'} aria-label={'Download CC'}>
                                {isCCLoading ? (
                                    <Spinner size={'xs'} />
                                ) : (
                                    <span>
                                        <Icon fontSize={'20px'} highlightOnHover icon="closed-captioning" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>
                        <Box onClick={onOpen} fontSize={'0.875rem'} color={'#cc0000'} fontWeight={500} cursor={'pointer'}>
                            <Tooltip hasArrow label={'Delete quiz'} aria-label={'Delete quiz'}>
                                <span>
                                    <Icon highlightOnHover icon="trash" />
                                </span>
                            </Tooltip>
                        </Box>
                    </Flex>
                </Td>
            </Tr>
        </>
    );
};
